/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { node, shape, string, func, bool, arrayOf, object } from 'prop-types';
import classNames from 'clsx';
import { connect } from 'react-redux';

import { createUrl } from 'utils/urlHelper';
import { getWebPImage } from 'utils/image';

import RequestButton from 'components/RequestButton/RequestButton';
import LikeButton from 'components/LikeButton/LikeButton';
import ManageButton from 'components/ManageButton/ManageButton';
import ChatButton from 'components/ChatButton/ChatButton';

import ImageSlider from './ImageSlider/ImageSlider';
import SignUpImage from '../../../media/jpg/sign-up-card.jpg';

import styles from './profile-card.scss';

/**
 * This is the ProfileCard component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */

const HandleClick = ({ children, onClick, slug }) => {
  if (onClick) {
    return (
      <div className={styles.link} onClick={onClick}>
        {children}
      </div>
    );
  }

  return (
    <NavLink
      className={styles.link}
      to={slug ? createUrl('profile', slug) : createUrl('signup')}
    >
      {children}
    </NavLink>
  );
};

HandleClick.propTypes = {
  onClick: func,
  slug: string.isRequired,
  children: node.isRequired,
};

HandleClick.defaultProps = {
  onClick: null,
};

const ProfileCard = ({
  talent_id,
  title,
  info,
  slug,
  jobId,
  discussion_id,
  show_chat_button,
  show_like_button,
  show_manage_button,
  show_request_button,
  show_unlike_button,
  onClick,
  picture: { medium_url: image },
  pictures,
  hideOptions,
  user,
}) => {
  const defaultPicture = pictures.filter(picture => picture.is_default);
  const notDefaultPictures = pictures.filter(picture => !picture.is_default);

  const sortedPictures = [...defaultPicture, ...notDefaultPictures];

  return (
    <article className={styles.wrapper}>
      <HandleClick slug={slug} onClick={talent_id ? null : onClick}>
        <div className={styles.container}>
          {title === 'And many more...' ? (
            <ImageSlider>
              {[
                <div
                  key="image-0"
                  className={styles.image}
                  style={{
                    backgroundImage: `url(${SignUpImage})`,
                  }}
                />,
                <div
                  key="image-0"
                  className={styles.image}
                  style={{
                    backgroundImage: `url(${SignUpImage})`,
                  }}
                />,
              ]}
            </ImageSlider>
          ) : (
            <ImageSlider>
              {talent_id && sortedPictures.length
                ? sortedPictures.map(({ medium_url }, i) => (
                    <div
                      key={`image-${i}`}
                      className={styles.image}
                      style={{
                        backgroundImage: `url(${getWebPImage(medium_url)})`,
                      }}
                    />
                  ))
                : [
                    <div
                      key="image-0"
                      className={styles.image}
                      style={{
                        backgroundImage: `url(${getWebPImage(image)})`,
                      }}
                    />,
                  ]}
            </ImageSlider>
          )}

          {(show_like_button || show_unlike_button) && (
            <div className={styles.likeButton}>
              <LikeButton
                micro
                showIcon
                slug={slug}
                talentId={talent_id}
                jobId={jobId}
                active={show_unlike_button}
              />
            </div>
          )}

          {!hideOptions && (
            <div className={styles.options}>
              {show_request_button && !user.is_talent && (
                <RequestButton
                  micro
                  dark
                  slug={slug}
                  talentId={talent_id}
                  jobId={jobId}
                />
              )}

              {show_manage_button && (
                <ManageButton dark micro talentId={talent_id} />
              )}

              {show_chat_button && (
                <ChatButton micro jobId={jobId} discussionId={discussion_id} />
              )}
            </div>
          )}
        </div>

        {talent_id && (
          <header
            className={classNames(styles.header, {
              [styles.extended]: !!info,
            })}
          >
            <h2 className={styles.name}>{title}</h2>
            {info && <p className={styles.title}>{info}</p>}
          </header>
        )}
      </HandleClick>
    </article>
  );
};

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
ProfileCard.propTypes = {
  title: string.isRequired,
  info: string.isRequired,
  picture: shape({
    square_url: string.isRequired,
  }).isRequired,
  pictures: arrayOf(object),
  talent_id: string.isRequired,
  slug: string.isRequired,
  discussion_id: string,
  show_chat_button: bool,
  show_like_button: bool,
  show_manage_button: bool,
  show_request_button: bool,
  show_unlike_button: bool,
  hideOptions: bool,
  jobId: string,
  onClick: func,
  user: object,
};

/**
 * Declare default props.
 *
 * @type {Object}
 */
ProfileCard.defaultProps = {
  discussion_id: null,
  show_chat_button: false,
  show_like_button: false,
  show_manage_button: false,
  show_request_button: false,
  show_unlike_button: false,
  jobId: null,
  hideOptions: false,
  children: null,
  onClick: null,
  pictures: [],
  user: null,
};

export default connect(({ user }) => ({ user }))(ProfileCard);
